import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { authFetch } from "../../../services/authFetch";
import { DateTimeField, toLocalValue } from "../../common/components/DateTimeField";

export interface ExportMarkersFormProps {
  projectId: string | null;
  projectName?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  isActive?: boolean;
}

interface ExportMarkersDialogProps {
  open: boolean;
  projectId: string | null;
  projectName: string;
  onClose: () => void;
}

export const ExportMarkersForm: React.FC<ExportMarkersFormProps> = ({
  projectId,
  projectName,
  onSuccess,
  onCancel,
  isActive = true,
}) => {
  const { t } = useTranslation();

  const [fromValue, setFromValue] = useState("");
  const [toValue, setToValue] = useState("");
  const [count, setCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isActive) return;
    const now = new Date();
    const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
    setFromValue(toLocalValue(twoHoursAgo));
    setToValue(toLocalValue(now));
    setCount(null);
    setError(null);
  }, [isActive]);

  const rangeIso = useMemo(() => {
    if (!fromValue || !toValue) return null;
    const from = new Date(fromValue);
    const to = new Date(toValue);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    if (from >= to) return null;
    return { from: from.toISOString(), to: to.toISOString() };
  }, [fromValue, toValue]);

  useEffect(() => {
    if (!isActive || !projectId || !rangeIso) {
      setCount(null);
      return;
    }
    let cancelled = false;
    setCountLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await authFetch(
          `/api/projects/${projectId}/markers/count?from=${encodeURIComponent(rangeIso.from)}&to=${encodeURIComponent(rangeIso.to)}`,
        );
        if (cancelled) return;
        if (!res.ok) {
          setCount(null);
          setError(`Preview failed (${res.status})`);
          return;
        }
        const data = await res.json();
        setCount(typeof data.count === "number" ? data.count : 0);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isActive, projectId, rangeIso]);

  const handleDownload = async () => {
    if (!projectId || !rangeIso) return;
    setDownloading(true);
    setError(null);
    try {
      const res = await authFetch(
        `/api/projects/${projectId}/markers/export?format=avid&from=${encodeURIComponent(rangeIso.from)}&to=${encodeURIComponent(rangeIso.to)}`,
      );
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || "markers.txt";

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const rangeInvalid = !rangeIso;

  return (
    <div className="flex flex-col gap-3 w-full">
      <DateTimeField
        value={fromValue}
        onChange={setFromValue}
        testId="export-markers-from"
        label={t("logger.export.from", "Desde")}
      />
      <DateTimeField
        value={toValue}
        onChange={setToValue}
        testId="export-markers-to"
        label={t("logger.export.to", "Hasta")}
      />

      <div className="text-sm text-[#ccc] text-left" data-testid="export-markers-count">
        {rangeInvalid
          ? t("logger.export.invalidRange", "Rango inválido")
          : countLoading
            ? t("logger.export.counting", "Contando…")
            : count === null
              ? "…"
              : t("logger.export.countLabel", "{{count}} markers en el rango", {
                  count,
                })}
      </div>

      {error && (
        <div
          className="text-sm text-[var(--color-accent-danger,#ff5555)] text-left"
          data-testid="export-markers-error"
        >
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={downloading}>
            {t("common.dialog.cancel", "Cancelar")}
          </Button>
        )}
        <Button
          onClick={handleDownload}
          disabled={rangeInvalid || downloading || !projectId || count === 0}
          data-testid="export-markers-download"
          className="bg-[var(--color-accent-success)] text-black hover:bg-[var(--color-accent-success)]/90"
        >
          {downloading
            ? t("logger.export.downloading", "Descargando…")
            : t("logger.export.download", "Descargar .txt")}
        </Button>
      </div>
    </div>
  );
};

export const ExportMarkersDialog: React.FC<ExportMarkersDialogProps> = ({
  open,
  projectId,
  projectName,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[480px]" data-testid="export-markers-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--color-accent-warning)] text-lg font-black uppercase tracking-wider">
            {t("logger.export.title", "Exportar markers")}
          </DialogTitle>
          <DialogDescription className="text-[#ccc] text-[0.95rem] leading-relaxed">
            {t(
              "logger.export.description",
              "AVID Media Composer (.txt). Selecciona el rango de hora de logueo.",
            )}
            {projectName ? ` · ${projectName}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <ExportMarkersForm
            projectId={projectId}
            projectName={projectName}
            isActive={open}
            onSuccess={onClose}
            onCancel={onClose}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportMarkersDialog;
